/**
 * Shared modal-orbit motion tokens.
 *
 * Every timing, easing, and stagger value used across the modal
 * choreography lives here. Import from this module so any future modal
 * automatically inherits the same spec.
 *
 * Design brief:
 *   - Modal shell scales + fades in over 100ms (ease-out)
 *   - After the shell settles, children (buttons, peek art, etc.)
 *     start arriving in a 20ms per-row cascade
 *   - Individual entrances are gentle (150ms opacity + tiny y lift)
 *   - Everything runs symmetrically on close
 */

// -------- Raw tokens (single source of truth) --------
export const MODAL_DURATION_MS = 100;
export const MODAL_DELAY_MS = 100;              // wait before children start
export const ROW_STAGGER_MS = 20;               // per-row stagger for the bento grid
export const BUTTON_ENTRANCE_DURATION_MS = 150;
export const PEEK_ART_FADE_DURATION_MS = 150;
export const HOVER_FEEDBACK_DURATION_MS = 150;
export const EASE = 'easeOut';

// framer-motion consumes seconds, not ms. Precompute so we don't
// scatter `/1000` divisions across the codebase.
export const MODAL_DURATION = MODAL_DURATION_MS / 1000;
export const MODAL_DELAY = MODAL_DELAY_MS / 1000;
export const ROW_STAGGER = ROW_STAGGER_MS / 1000;
export const BUTTON_ENTRANCE_DURATION = BUTTON_ENTRANCE_DURATION_MS / 1000;
export const PEEK_ART_FADE_DURATION = PEEK_ART_FADE_DURATION_MS / 1000;
export const HOVER_FEEDBACK_DURATION = HOVER_FEEDBACK_DURATION_MS / 1000;

/**
 * Shared className string applied to every Radix DialogContent in the
 * app so all modals open + close on the same 100ms scale-and-fade
 * rhythm. Keeps the diagonal slide-in transforms zeroed (pure centered
 * scale) and gates on `motion-reduce:` for accessibility.
 *
 * Usage:
 * ```jsx
 * <DialogContent className={`${MODAL_DIALOG_MOTION_CLASSES} <extra layout classes>`} ... />
 * ```
 *
 * Any tweak to modal choreography — timing, easing, reduced-motion —
 * lives here so every modal updates in lockstep.
 */
export const MODAL_DIALOG_MOTION_CLASSES =
  '!duration-100 data-[state=open]:!slide-in-from-left-0 data-[state=open]:!slide-in-from-top-0 data-[state=closed]:!slide-out-to-left-0 data-[state=closed]:!slide-out-to-top-0 ease-out data-[state=closed]:ease-in motion-reduce:!duration-0 motion-reduce:!animate-none';

// -------- Ready-to-use variant factories --------

/**
 * Bento parent — empty variants object exists purely so children with
 * `variants={...}` inherit the animation state (initial→visible)
 * without us having to pass initial/animate to every child.
 */
export const BENTO_PARENT_VARIANTS = {
  hidden: {},
  visible: {},
};

/**
 * Per-button variants for the bento cascade. `custom` is the numeric
 * row index (0-based) — pairs in the same row animate together, then
 * the next row starts ROW_STAGGER_MS later.
 */
export const BENTO_BTN_VARIANTS = {
  hidden: { opacity: 0, y: 6 },
  visible: (rowIndex) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: MODAL_DELAY + (rowIndex || 0) * ROW_STAGGER,
      duration: BUTTON_ENTRANCE_DURATION,
      ease: EASE,
    },
  }),
};

/**
 * Peek-shell entrance — the tilted thumbnail card next to the modal.
 * Fades + scales in with the modal shell so the whole "orbit" arrives
 * as a unit.
 *
 * @param {number} rotate - Tilt in degrees. Prev = -4, next = +4.
 */
export function makePeekShellVariants(rotate) {
  return {
    initial: { opacity: 0, scale: 0.9, rotate, y: '-50%' },
    animate: { opacity: 0.45, scale: 0.96, rotate, y: '-50%' },
    whileHover: {
      opacity: 0.85,
      scale: 0.98,
      transition: { duration: HOVER_FEEDBACK_DURATION },
    },
    whileFocus: {
      opacity: 0.9,
      transition: { duration: HOVER_FEEDBACK_DURATION },
    },
    transition: {
      delay: MODAL_DELAY,
      duration: MODAL_DURATION,
      ease: EASE,
    },
  };
}

/**
 * Peek album art — arrives a beat AFTER the shell settles so the tile
 * "materialises from mask to art" instead of appearing fully formed.
 * Start delay = shell delay + shell duration.
 */
export const PEEK_ART_MOTION = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: {
    delay: MODAL_DELAY + MODAL_DURATION,
    duration: PEEK_ART_FADE_DURATION,
    ease: EASE,
  },
};
